/**
 * FT-269: timezone utilities for voyage event display + snapshotting.
 *
 * Voyage events store the event time in UTC ISO 8601 plus a frozen
 * `eventTimezone` (numeric offset like '+0100'). Display always derives
 * from those two together, never from the device's current timezone or
 * from the vessel's current setting (which may have changed since the
 * event was recorded).
 *
 * All exports are pure functions with no side effects.
 */

/**
 * Curated list of common operating timezones surfaced in the vessel
 * settings dropdown. Ordering is deliberate: UK/NW Europe first
 * (current customer base), then other relevant operating regions.
 * "Other..." case is handled by free-text input in the UI.
 */
export const COMMON_TIMEZONES = [
  'Europe/Belfast',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Amsterdam',
  'Europe/Lisbon',
  'Atlantic/Reykjavik',
  'Atlantic/Faroe',
  'America/New_York',
  'Asia/Singapore',
  'Asia/Dubai',
  'Pacific/Auckland',
  'Australia/Sydney',
  'UTC'
];

/**
 * Resolve the numeric offset string (e.g. '+0100', '-0500') for a given
 * IANA timezone at a given instant. Returns '+0000' on any error
 * (invalid timezone, missing Intl, etc.) so callers never crash.
 *
 * @param {string} timeZone - IANA name, e.g. 'Europe/Belfast'
 * @param {Date} [atDate=new Date()] - the instant to resolve the offset for
 * @returns {string} numeric offset like '+0100'
 */
export function getOffsetForTimezone(timeZone, atDate = new Date()) {
  try {
    if (!timeZone || typeof Intl === 'undefined' || !Intl.DateTimeFormat) {
      return '+0000';
    }
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      timeZoneName: 'shortOffset'
    }).formatToParts(atDate);
    const tzName = parts.find(p => p.type === 'timeZoneName')?.value || '';
    // tzName is like "GMT+1", "GMT-5", "GMT" (== UTC), "GMT+05:30"
    const match = tzName.match(/^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/);
    if (!match) {
      // "GMT" with no suffix is UTC; anything else falls back to +0000.
      return tzName === 'GMT' ? '+0000' : '+0000';
    }
    const sign = match[1];
    const hh = match[2].padStart(2, '0');
    const mm = (match[3] || '00').padStart(2, '0');
    return `${sign}${hh}${mm}`;
  } catch {
    return '+0000';
  }
}

/**
 * Resolve a human-readable offset label for a given IANA timezone (e.g.
 * "GMT+1"). Used in dropdown labels, NOT for storage.
 *
 * @param {string} timeZone
 * @param {Date} [atDate=new Date()]
 * @returns {string} e.g. 'GMT+1'
 */
export function getShortOffsetLabel(timeZone, atDate = new Date()) {
  try {
    if (!timeZone || typeof Intl === 'undefined' || !Intl.DateTimeFormat) {
      return 'GMT';
    }
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      timeZoneName: 'shortOffset'
    }).formatToParts(atDate);
    return parts.find(p => p.type === 'timeZoneName')?.value || 'GMT';
  } catch {
    return 'GMT';
  }
}

/**
 * Format a UTC ISO 8601 timestamp in a specific IANA timezone.
 * Tolerates a numeric offset string in place of IANA (falls back to UTC
 * display with the offset baked into the formatted string).
 *
 * @param {string} isoUtc - UTC ISO 8601 string, e.g. '2026-05-15T13:32:00.000Z'
 * @param {string} timeZone - IANA name (preferred) OR numeric offset '+0100'
 * @param {Object} [options]
 * @param {boolean} [options.withOffset=false] - append " GMT+1"
 * @param {boolean} [options.withUtcHint=false] - append " (HH:MM UTC)"
 * @param {string} [options.format='datetime'] - 'datetime' | 'date' | 'time' | 'YYYY-MM-DD'
 * @returns {string}
 */
export function formatDateInTimezone(isoUtc, timeZone, options = {}) {
  if (!isoUtc) return '';
  const {
    withOffset = false,
    withUtcHint = false,
    format = 'datetime'
  } = options;

  let date;
  try {
    date = new Date(isoUtc);
    if (isNaN(date.getTime())) return '';
  } catch {
    return '';
  }

  // If timeZone is a numeric offset (e.g. '+0100'), interpret manually.
  // The Intl API doesn't accept '+0100' as a timeZone string; for those
  // cases we shift the UTC instant by the offset and then format as UTC.
  let tzForIntl = timeZone;
  if (typeof timeZone === 'string' && /^[+-]\d{4}$/.test(timeZone)) {
    const sign = timeZone[0] === '-' ? -1 : 1;
    const hours = parseInt(timeZone.slice(1, 3), 10);
    const mins = parseInt(timeZone.slice(3, 5), 10);
    const shiftedMs = date.getTime() + sign * (hours * 60 + mins) * 60_000;
    date = new Date(shiftedMs);
    tzForIntl = 'UTC';
  } else if (!timeZone) {
    tzForIntl = 'UTC';
  }

  let formatted;
  try {
    switch (format) {
      case 'date':
        formatted = new Intl.DateTimeFormat('en-GB', {
          timeZone: tzForIntl,
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }).format(date);
        break;
      case 'time':
        formatted = new Intl.DateTimeFormat('en-GB', {
          timeZone: tzForIntl,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).format(date);
        break;
      case 'YYYY-MM-DD': {
        // Build YYYY-MM-DD from parts so we don't get locale surprises.
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: tzForIntl,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).formatToParts(date);
        const y = parts.find(p => p.type === 'year')?.value || '0000';
        const m = parts.find(p => p.type === 'month')?.value || '00';
        const d = parts.find(p => p.type === 'day')?.value || '00';
        formatted = `${y}-${m}-${d}`;
        break;
      }
      case 'datetime':
      default:
        formatted = new Intl.DateTimeFormat('en-GB', {
          timeZone: tzForIntl,
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).format(date);
        break;
    }
  } catch {
    // Last-resort fallback: ISO string truncated.
    formatted = isoUtc.replace('T', ' ').slice(0, 16);
  }

  let suffix = '';
  if (withOffset && typeof timeZone === 'string') {
    if (/^[+-]\d{4}$/.test(timeZone)) {
      const sign = timeZone[0];
      const hh = parseInt(timeZone.slice(1, 3), 10);
      const mm = timeZone.slice(3, 5);
      suffix += ` GMT${sign}${hh}${mm === '00' ? '' : `:${mm}`}`;
    } else {
      suffix += ` ${getShortOffsetLabel(timeZone, new Date(isoUtc))}`;
    }
  }

  if (withUtcHint) {
    try {
      const utcDate = new Date(isoUtc);
      const utcHHMM = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'UTC',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(utcDate);
      suffix += ` (${utcHHMM} UTC)`;
    } catch {
      // Skip the hint if we can't compute it.
    }
  }

  return formatted + suffix;
}

/**
 * Resolve a vessel's effective timezone for display, with safe fallback.
 * Used everywhere a vessel record may carry a missing or invalid
 * `timezone` setting.
 *
 * @param {Object} vessel - the merged currentVessel object
 * @returns {string} IANA name
 */
export function resolveVesselTimezone(vessel) {
  const candidate = vessel?.timezone || vessel?.settings?.timezone;
  if (candidate) {
    // Sanity-check the IANA name by attempting to resolve an offset.
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: candidate }).format(new Date());
      return candidate;
    } catch {
      // Fall through to device default.
    }
  }
  return (typeof Intl !== 'undefined' && Intl.DateTimeFormat
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'UTC') || 'UTC';
}

/**
 * Convert a local date+time entered in a given IANA timezone to the
 * corresponding UTC ISO 8601 string. Used by TimePickerModal when the
 * Master enters an actual event time.
 *
 * Approach: build a probe Date for the local fields treated as UTC,
 * compute the offset at that instant for the target tz, then shift.
 *
 * @param {string} localDate - 'YYYY-MM-DD'
 * @param {string} localTime - 'HH:MM' (24h)
 * @param {string} timeZone - IANA name
 * @returns {string} UTC ISO 8601 string
 */
export function localTimeToUtcIso(localDate, localTime, timeZone) {
  if (!localDate || !localTime) return new Date().toISOString();
  try {
    // Build a probe instant assuming the local fields are already UTC.
    const probe = new Date(`${localDate}T${localTime}:00Z`);
    if (isNaN(probe.getTime())) return new Date().toISOString();

    const offset = getOffsetForTimezone(timeZone, probe);
    const sign = offset[0] === '-' ? -1 : 1;
    const hh = parseInt(offset.slice(1, 3), 10);
    const mm = parseInt(offset.slice(3, 5), 10);
    const offsetMinutes = sign * (hh * 60 + mm);

    // The probe is `localFields as UTC`. The true UTC instant is
    // probe - offsetMinutes (offset is local-relative-to-UTC).
    const utcMs = probe.getTime() - offsetMinutes * 60_000;
    return new Date(utcMs).toISOString();
  } catch {
    return new Date().toISOString();
  }
}
